// Map layer metrics harness (BBB-523, §5 item 2 of the 14/08 plan).
//
// Answers one question before anyone optimises anything: on a real pan or zoom,
// does the time go on the network, on refetching what we already had, or on
// building Maps SDK objects? Measured toggle latency is 0.8-1.2s and warm is not
// faster than cold, which points at the missing viewport cache rather than at
// render cost — but that is an inference, and this replaces it with numbers.
//
// OFF by default and free when off: every entry point returns early before it
// allocates anything, and nothing here runs unless a developer turns it on.
//
// Turning it on, in the browser console on any environment including uat:
//
//   localStorage.setItem('bbb:map-metrics', '1')   // survives reload
//   window.__bbbMapMetrics = true                  // this page only
//
// and off again with removeItem, or setting the flag to false. Deliberately not
// a build-time env var: the interesting measurements are on a deployed
// environment with real network latency, where rebuilding to flip a flag is not
// practical.
//
// Printing goes through report.js like everything else in the feature, but on
// reportGroup, the one path there that is not gated on NODE_ENV. That flag above
// is this harness's gate, and it has to be, for the reason in the paragraph
// directly above this one.

import { reportGroup } from './report';

const STORAGE_KEY = 'bbb:map-metrics';

/**
 * Whether to collect. Read fresh each time so the console toggle takes effect
 * without a reload, and defensively wrapped because localStorage throws in
 * private-mode Safari rather than returning null.
 */
export function isMetricsEnabled() {
  if (typeof window === 'undefined') return false;
  if (window.__bbbMapMetrics === true) return true;
  if (window.__bbbMapMetrics === false) return false;
  try {
    return window.localStorage?.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Monotonic clock where available; Date.now is only a fallback for old jsdom. */
function now() {
  return typeof performance !== 'undefined' && performance.now
    ? performance.now()
    : Date.now();
}

let gesture = null;
let gestureCount = 0;

/**
 * Start timing one user gesture — a pan or zoom that has settled into `idle`.
 *
 * @param {object} args
 * @param {number} args.expected how many layers are about to be refreshed
 * @param {number} [args.zoom]
 * @param {string} [args.trigger] what caused it, e.g. 'idle' or 'reconcile'
 * @returns {boolean} whether collection started
 */
export function beginGesture({ expected, zoom, trigger = 'idle' }) {
  if (!isMetricsEnabled()) return false;
  // An idle with no vector layers live costs nothing and has nothing to report.
  // Opening a gesture for it would also mean it never reaches its expected
  // count, so the next real gesture would flush it as "superseded" and every
  // summary would be preceded by an empty table.
  if (!expected) return false;

  // A gesture already in flight means layers from the previous one have not all
  // reported. Flush it rather than discard it, so a slow layer still shows up
  // somewhere and the counts stay honest.
  if (gesture) flushGesture('superseded');

  gestureCount += 1;
  gesture = {
    id: gestureCount,
    startedAt: now(),
    expected,
    zoom,
    trigger,
    layers: [],
  };
  return true;
}

/**
 * Record one layer's completed refresh.
 *
 * @param {string} sourceId
 * @param {object} stats
 * @param {number} [stats.fetchMs] time in the network call, across all pages
 * @param {number} [stats.bytes] uncompressed response bytes, across all pages
 * @param {number} [stats.pages] how many pages were requested
 * @param {number} [stats.features] features returned
 * @param {number} [stats.shapes] Maps SDK objects created
 * @param {number} [stats.renderMs] time spent building those objects
 * @param {boolean} [stats.truncated] whether the page cap was hit
 * @param {string} [stats.outcome] 'rendered' | 'superseded' | 'failed'
 */
export function recordLayer(sourceId, stats = {}) {
  if (!isMetricsEnabled() || !gesture) return;
  gesture.layers.push({ sourceId, ...stats });
  if (gesture.layers.length >= gesture.expected) flushGesture('complete');
}

/**
 * Print the gesture and clear it. Exported so a caller can force a flush when
 * a layer will never report — a failed fetch that returns before the adapter
 * gets far enough to record, for instance.
 *
 * @param {string} [reason]
 * @returns {object|null} the flushed gesture, for tests
 */
export function flushGesture(reason = 'complete') {
  if (!gesture) return null;

  const flushed = {
    ...gesture,
    reason,
    wallMs: round(now() - gesture.startedAt),
  };
  gesture = null;

  const totals = flushed.layers.reduce(
    (acc, layer) => ({
      fetchMs: acc.fetchMs + (layer.fetchMs || 0),
      bytes: acc.bytes + (layer.bytes || 0),
      features: acc.features + (layer.features || 0),
      shapes: acc.shapes + (layer.shapes || 0),
      renderMs: acc.renderMs + (layer.renderMs || 0),
    }),
    { fetchMs: 0, bytes: 0, features: 0, shapes: 0, renderMs: 0 },
  );

  // Wall time is what the farmer waits; the fetch total is the sum across
  // layers running in parallel, so it exceeds wall time and is NOT a share of
  // it. Printed side by side rather than as a percentage for that reason.
  reportGroup({
    headline:
      `[map-metrics] gesture ${flushed.id} (${flushed.trigger}, z${flushed.zoom ?? '?'}) ` +
      `${flushed.wallMs}ms wall · ${flushed.layers.length}/${flushed.expected} layers · ` +
      `${formatBytes(totals.bytes)} · ${totals.shapes} shapes`,
    rows: flushed.layers.map((layer) => ({
      layer: layer.sourceId,
      fetchMs: round(layer.fetchMs),
      renderMs: round(layer.renderMs),
      bytes: layer.bytes,
      features: layer.features,
      shapes: layer.shapes,
      pages: layer.pages,
      truncated: layer.truncated,
      outcome: layer.outcome,
    })),
    note:
      reason === 'complete'
        ? undefined
        : `[map-metrics] gesture ${flushed.id} flushed as "${reason}"`,
  });

  return flushed;
}

function round(value) {
  return typeof value === 'number' ? Math.round(value * 10) / 10 : value;
}

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/** Test seam: drop any gesture in flight without printing. */
export function resetMetrics() {
  gesture = null;
  gestureCount = 0;
}
